import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Crown, Copy, Check, ShieldCheck, CalendarClock, Coins, AlertTriangle } from "lucide-react";

export default function Membership() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [txid, setTxid] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/membership").then(({ data }) => setInfo(data)).catch(() => {});
  }, []);

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  const expiresAt = user?.membership_expires_at ? new Date(user.membership_expires_at) : null;
  const isActive = user?.is_member;
  const expiryLabel = expiresAt
    ? expiresAt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : null;

  const pay = async () => {
    if (!txid.trim()) { toast.error("Paste your USDT transaction hash"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/membership/pay", { txid });
      toast.success(isActive ? "Membership renewed! 🎉" : "Monthly membership activated! 🎉");
      await checkAuth();
      setTxid("");
      if (!isActive) navigate("/dashboard");
      else if (data?.membership_expires_at) {
        // stay to show updated expiry
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const perks = [
    "Verified gold member badge on your profile",
    "Priority placement & trust signal to trade partners",
    "List items and send trade offers for a full month",
  ];

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10">
      <div className="bg-black text-white rounded-2xl p-8 border-2 border-[#E6B31E] nb-shadow-lg">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-xl bg-[#E6B31E] text-black flex items-center justify-center"><Crown size={28} strokeWidth={2.5} /></div>
          <div>
            <h1 className="font-display font-black text-3xl tracking-tighter text-[#E6B31E]">Monthly Membership</h1>
            <p className="text-white/60 font-medium text-sm">Required to trade — 7 USDT per month, renew anytime.</p>
          </div>
        </div>

        {isActive && (
          <div className="mt-8 flex items-center gap-3 bg-[#E6B31E] text-black rounded-xl p-5 font-extrabold" data-testid="membership-active-banner">
            <ShieldCheck size={24} />
            <div>
              <div>Your membership is active.</div>
              {expiryLabel && <div className="text-sm font-bold flex items-center gap-1.5"><CalendarClock size={14} /> Renews / expires on {expiryLabel}</div>}
            </div>
          </div>
        )}

        {!isActive && user && (
          <div className="mt-5 bg-[#E6B31E] text-black rounded-xl px-4 py-3 text-sm font-bold" data-testid="membership-cta">
            Activate your 7 USDT/month membership (paid in USDT on the Tron / TRX network) to unlock listing items and sending trade offers.
          </div>
        )}

        <div className="mt-6 flex items-baseline gap-2">
          <span className="font-display font-black text-5xl text-white">{info?.price ?? "—"}</span>
          <span className="font-extrabold text-[#E6B31E]">USDT</span>
          <span className="text-white/40 font-semibold text-sm">/ month</span>
        </div>

        <div className="mt-3 inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1.5 text-xs font-bold text-[#E6B31E]" data-testid="membership-network-chip">
          <Coins size={14} strokeWidth={2.5} /> USDT on the Tron (TRX) network — TRC-20
        </div>

        <ul className="mt-5 space-y-2">
          {perks.map((p, i) => (
            <li key={i} className="flex items-center gap-2 text-sm font-medium text-white/80">
              <Check size={16} className="text-[#E6B31E] flex-shrink-0" strokeWidth={3} /> {p}
            </li>
          ))}
        </ul>

        {info && (
          <div className="mt-6">
            <div className="text-xs font-bold uppercase text-white/50">Send exactly {info.price} USDT (TRC-20) to</div>
            <button onClick={() => copy(info.wallet)} className="w-full mt-1 flex items-center justify-between gap-2 bg-white/10 rounded-lg px-3 py-2.5 text-sm font-mono break-all" data-testid="membership-wallet">
              <span className="truncate">{info.wallet}</span>
              <Copy size={16} className="flex-shrink-0 text-[#E6B31E]" />
            </button>

            <div className="mt-3 flex items-start gap-2 bg-red-500/15 border border-red-500/40 rounded-lg px-3 py-2.5" data-testid="membership-network-warning">
              <AlertTriangle size={16} className="text-red-400 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
              <p className="text-[12px] font-semibold text-red-200 leading-snug">
                Send <span className="font-black">USDT on the Tron (TRX) network — TRC-20 only</span>.
                Sending on any other network (ERC-20 / Ethereum, BEP-20 / BNB, etc.) will result in
                <span className="font-black"> permanent loss of funds</span>.
              </p>
            </div>

            <p className="text-[11px] text-white/40 font-medium mt-2">
              Funds go directly to the exchange's wallet. After sending, paste your transaction hash below to {isActive ? "extend your membership by another month" : "unlock membership"}.
            </p>

            <input data-testid="membership-txid" value={txid} onChange={(e) => setTxid(e.target.value)} placeholder="Paste transaction hash (TXID)"
              className="w-full mt-3 p-3 rounded-xl bg-white text-black font-medium outline-none" />
            <button data-testid="membership-pay-btn" disabled={busy} onClick={pay}
              className="w-full mt-2 bg-[#E6B31E] text-black font-extrabold rounded-full py-3.5 border-2 border-black disabled:opacity-50">
              {busy ? "Verifying…" : isActive ? "I've paid — renew for another month" : "I've paid — unlock membership"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
